import { Fragment, useCallback, useEffect } from 'react';
import { ASSISTANT_PANEL_WIDTH } from './AiAssistant/AssistantPanel';
import { Button } from '@/ui/design-system/components/ui/button';
import { Transition } from '@headlessui/react';
import { X } from 'lucide-react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { useAssistantContext } from './AiAssistant/AssistantProvider';

// One width for every row drawer, so an explore drawer and a related-entity
// drawer opened from the same page are the same size. The chart "drivers of
// growth" drawers pass their own 42rem and are a different family.
export const DRAWER_WIDTH = '36rem';

function Drawer({ children, open, $width = DRAWER_WIDTH, ...props }) {
    const { isPanelOpen } = useAssistantContext();
    const rightOffset = isPanelOpen ? ASSISTANT_PANEL_WIDTH : 0;

    const handleEscape = useCallback(
        (e) => {
            if (e.key === 'Escape') props.onClose?.();
        },
        [props.onClose],
    );

    useEffect(() => {
        if (open) {
            document.addEventListener('keydown', handleEscape);
            return () => document.removeEventListener('keydown', handleEscape);
        }
    }, [open, handleEscape]);

    const drawerContent = (
        <Transition appear show={open} as={Fragment}>
            <DrawerWrapper role="dialog" aria-modal="true" {...props}>
                <TransitionWrapper>
                    <Transition.Child
                        as={Fragment}
                        enter="bg-enter"
                        enterFrom="bg-first"
                        enterTo="bg-final"
                        leave="bg-leave"
                        leaveFrom="bg-final"
                        leaveTo="bg-first"
                    >
                        <Backdrop aria-hidden="true" onClick={props.onClose} />
                    </Transition.Child>
                    <Transition.Child
                        as={Fragment}
                        enter="paper-enter"
                        enterFrom="paper-first"
                        enterTo="paper-final"
                        leave="paper-leave"
                        leaveFrom="paper-final"
                        leaveTo="paper-first"
                    >
                        <PaperPosition $width={$width}>
                            <Button
                                variant="secondary"
                                size="icon"
                                onClick={props.onClose}
                                className="absolute -left-[52px] top-4"
                                aria-label="Close drawer"
                            >
                                <X className="size-4" />
                            </Button>
                            <Paper>{children}</Paper>
                        </PaperPosition>
                    </Transition.Child>
                </TransitionWrapper>
            </DrawerWrapper>
        </Transition>
    );

    if (typeof document === 'undefined') return drawerContent;

    return createPortal(drawerContent, document.body);
}

const TransitionWrapper = styled.div`
    & .paper-enter {
        transition: transform 0.15s ease-out;
    }

    & .paper-first {
        transform: translateX(100%);
    }

    & .paper-final {
        transform: translateX(0);
    }

    & .paper-leave {
        transition: transform 0.2s ease-in;
    }

    & .bg-enter {
        transition: opacity 0.15s ease-out;
    }

    & .bg-first {
        opacity: 0;
    }

    & .bg-final {
        opacity: 1;
    }

    & .bg-leave {
        transition: opacity 0.2s ease-in;
    }
`;

const DrawerWrapper = styled.div`
    position: fixed;
    inset: 0;
    z-index: 50;
`;

const Backdrop = styled.div`
    position: fixed;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.2);
`;

const PaperPosition = styled.div`
    position: fixed;
    top: 0;
    right: ${({ $rightOffset }) => ($rightOffset ? `${$rightOffset}px` : '0')};
    height: 100vh;
    width: ${({ $width }) => $width};
    max-width: 100vw;
    display: flex;
    align-items: stretch;
    transition: right 0.3s ease-out;
`;

const Paper = styled.div`
    flex-grow: 1;
    background-color: ${({ theme }) => theme.gray_7};
    box-shadow: 0px 3px 7px rgba(0, 0, 0, 0.12);
    overflow-y: auto;
`;

export default Drawer;
